import { Box, Skeleton } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";

import { styled } from "@mui/material/styles";
import { renderSuccessMessage } from "../../lib/toastMessage";
import { copyToClipBoard, dateFormate, nameFormater } from "../../utils/common";

const CustomTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.grey[1000],
  color: theme.palette.grey[900],
  fontWeight: 700,
  fontSize: '1rem',
  letterSpacing: 0.2,
}));
const columns = [
  {id: "userId", label: "User ID", minWidth: 100},
  { id: "invoiceId", label: "Invoice Id", minWidth: 100 },
  { id: "paymentId", label: "Payment Id", minWidth: 100 },
  { id: "amount", label: "Amount", minWidth: 100 },
  { id: "currency", label: "Currency", minWidth: 100, align: "center" },
  { id: "status", label: "Status", minWidth: 100, align: "center" },
  { id: "paymentMethod", label: "Method", minWidth: 100, align: "center" },
  { id: "issuedAt", label: "Issued At", minWidth: 100, align: "center" },
];

export default function InvoiceTable({
  usersList = [],
  count,
  isListLoading,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  setLoad,
}) {
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setLoad(true);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
    setLoad(true);
  };

  const statusChip = (id, align, status) => (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "row",
        gap: "7px",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography>{nameFormater(status)}</Typography>
    </Box>
  );

  const methodChip = (id, align, value) => (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "row",
        gap: "7px",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography>{nameFormater(value)}</Typography>
    </Box>
  );

  const emailChip = (id, align, value) =>
    value ? (
      <Tooltip
        title="Check to copy"
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: "grey.300",
            },
          },
        }}
      >
        <Typography
          variant="inherit"
          onClick={() => {
            copyToClipBoard(value);
            renderSuccessMessage("Email copied!");
          }}
        >
          {value}
        </Typography>
      </Tooltip>
    ) : (
      "--"
    );

  const createdAtChip = (id, align, value) => {
    const { date, time } = dateFormate(value);

    return `${date}, ${time}`;
  };

  const userIdChip = (id, align, value) => {
    if (value && value.length > 50) {
      return (
        <Tooltip title={value}>
          <span>{`${value.substring(0, 30)}...`}</span>
        </Tooltip>
      );
    }
    return value || "--";
  };

  const defaultRowJsx = (id, align, value) => (value !== undefined ? value : "--");

  const tableRowData = {
    email: emailChip,
    createdAt: createdAtChip,
    issuedAt: createdAtChip,
    status: statusChip,
    method: methodChip,
    userId: userIdChip,
  };

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        borderRadius: "0px",
        gap: "20px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TableContainer
        sx={{
          maxHeight: window.innerHeight - 225,
        }}
      >
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead
            sx={{
              backgroundColor: "grey.1000",
              borderTop: "1px solid",
              borderBottom: "1px solid",
              borderColor: "grey.900",
            }}
          >
            <TableRow>
              {columns.map((column) => (
                <CustomTableCell key={column.id} align={column.align} style={{ minWidth: column.minWidth }}>
                  {column.label}
                </CustomTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!isListLoading &&
              Boolean(usersList.length) &&
              usersList.map((row, index) => (
                <TableRow hover role="checkbox" tabIndex={-1} key={row.code}>
                  {columns.map((column) => {
                    const { id } = column;
                    const value = row[id];
                    return (
                      <TableCell key={id} align={column.align} sx={{ color: 'grey.900', fontWeight: 500, fontSize: '0.97rem' }}>
                        {column.format && typeof value === "number"
                          ? column.format(value)
                          : (tableRowData?.[id] || defaultRowJsx)(id, column?.align, row[id], row, index)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}

            {/* Loading case */}
            {isListLoading && (
              <>
                <TableRow hover role="checkbox" tabIndex={-1}>
                  {columns.map((column) => {
                    const { id } = column;
                    return (
                      <TableCell key={id} align={column.align} sx={{ color: 'grey.700', fontWeight: 500 }}>
                        <Typography component="div" key="body1" variant="body1" sx={{ maxWidth: "70%", color: 'grey.700' }}>
                          <Skeleton />
                        </Typography>
                      </TableCell>
                    );
                  })}
                </TableRow>
                <TableRow hover role="checkbox" tabIndex={-1}>
                  {columns.map((column) => {
                    const { id } = column;
                    return (
                      <TableCell key={id} align={column.align} sx={{ color: 'grey.700', fontWeight: 500 }}>
                        <Typography component="div" key="body1" variant="body1" sx={{ maxWidth: "70%", color: 'grey.700' }}>
                          <Skeleton />
                        </Typography>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!usersList.length && !isListLoading && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <Typography sx={{ color: 'grey.800', fontWeight: 500 }}>No more data found!</Typography>
        </Box>
      )}

      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={count}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        SelectProps={{
          MenuProps: {
            sx: {
              ".MuiPaper-root": {
                color: "grey.900",
              },
            },
          },
        }}
        sx={{
          color: "grey.900",
          borderTop: "0.5px solid",
          borderColor: "grey.900",
        }}
      />
    </Paper>
  );
}

InvoiceTable.propTypes = {
  usersList: PropTypes.array,
  count: PropTypes.number,
  isListLoading: PropTypes.bool,
  page: PropTypes.number,
  setPage: PropTypes.func,
  rowsPerPage: PropTypes.number,
  setRowsPerPage: PropTypes.func,
  setLoad: PropTypes.func,
};
